import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { EmptyState, Panel, StatusChip } from '../../ui-kit';
import { fetchTenantAgents, type TenantAgent } from '../../services/tenantAgentsService';
import { AGENT_SECTIONS } from './AgentSidebar';
import SpotlightPage from './rocco/SpotlightPage';
import QueuePage from './rocco/QueuePage';
import CallbacksPage from './rocco/CallbacksPage';
import ConversationsPage from './rocco/ConversationsPage';
import ConfigurationPage from './rocco/ConfigurationPage';
import IntegrationsPage from './rocco/IntegrationsPage';
import PsSpotlightPage from './policy-servicing/SpotlightPage';
import { LiveRequestsPage, CompletedRequestsPage } from './policy-servicing/RequestsPage';
import PsConfigurationPage from './policy-servicing/ConfigurationPage';
import PsIntegrationsPage from './policy-servicing/IntegrationsPage';
import PsAccountsPage from './policy-servicing/AccountsPage';
import PsAccountPage from './policy-servicing/AccountPage';
import PcSpotlightPage from './policy-checking/SpotlightPage';
import NewCheckPage from './policy-checking/NewCheckPage';
import ChecksPage from './policy-checking/ChecksPage';
import PcConfigurationPage from './policy-checking/ConfigurationPage';
import PcIntegrationsPage from './policy-checking/IntegrationsPage';
import CqSpotlightPage from './carrier-quoting/SpotlightPage';
import NewQuotePage from './carrier-quoting/NewQuotePage';
import QuotesPage from './carrier-quoting/QuotesPage';
import QuotePage from './carrier-quoting/QuotePage';
import CqConfigurationPage from './carrier-quoting/ConfigurationPage';
import CqIntegrationsPage from './carrier-quoting/IntegrationsPage';
import CcSpotlightPage from './carrier-correspondence/SpotlightPage';
import CcCasesPage from './carrier-correspondence/CasesPage';
import CcConversationsPage from './carrier-correspondence/ConversationsPage';
import CcConfigurationPage from './carrier-correspondence/ConfigurationPage';
import CcIntegrationsPage from './carrier-correspondence/IntegrationsPage';
import McSpotlightPage from './missed-call/SpotlightPage';
import McConfigurationPage from './missed-call/ConfigurationPage';
import McIntegrationsPage from './missed-call/IntegrationsPage';
import { MissedCallsPage as McMissedCallsPage, CallsPage as McCallsPage } from './missed-call/CasesPage';

/** Each kind's pages, by section id. The first is where the agent opens. */
const PAGES: Record<string, Record<string, React.ComponentType>> = {
  rocco: {
    spotlight: SpotlightPage,
    configuration: ConfigurationPage,
    integrations: IntegrationsPage,
    queue: QueuePage,
    callbacks: CallbacksPage,
    conversations: ConversationsPage,
  },
  missed_call: {
    spotlight: McSpotlightPage,
    configuration: McConfigurationPage,
    integrations: McIntegrationsPage,
    cases: McMissedCallsPage,
    calls: McCallsPage,
  },
  policy_servicing: {
    spotlight: PsSpotlightPage,
    configuration: PsConfigurationPage,
    integrations: PsIntegrationsPage,
    live: LiveRequestsPage,
    completed: CompletedRequestsPage,
    accounts: PsAccountsPage,
  },
  policy_checking: {
    spotlight: PcSpotlightPage,
    configuration: PcConfigurationPage,
    integrations: PcIntegrationsPage,
    new: NewCheckPage,
    checks: ChecksPage,
  },
  carrier_correspondence: {
    spotlight: CcSpotlightPage,
    configuration: CcConfigurationPage,
    integrations: CcIntegrationsPage,
    cases: CcCasesPage,
    conversations: CcConversationsPage,
  },
  carrier_quoting: {
    spotlight: CqSpotlightPage,
    configuration: CqConfigurationPage,
    integrations: CqIntegrationsPage,
    new: NewQuotePage,
    quotes: QuotesPage,
  },
};

/** The sections that update by themselves, as things happen: only they wear the Live chip, pulsing. */
const STREAMING: Record<string, string[]> = {
  rocco: ['queue'],
  missed_call: ['cases', 'calls'],
  policy_servicing: ['live', 'completed'],
  policy_checking: ['checks'],
  carrier_correspondence: ['cases'],
  carrier_quoting: ['quotes'],
};

const titleOf = (kind: string, section: string): string =>
  (AGENT_SECTIONS[kind] ?? []).flatMap((group) => group.items).find((item) => item.id === section)?.label
  ?? 'Spotlight';

/**
 * One agent, with its own sections in the sidebar beside it.
 *
 * Opens for an agent this workspace has (`tenant_agents`), found by its kind
 * in the address — /agents/rocco/queue. Its name and description are the
 * workspace's own, so a rename on the Agents page shows here too.
 */
export default function AgentPage() {
  const { playbookId, agentKind } = useParams();
  const kind = agentKind || playbookId;
  const [agent, setAgent] = React.useState<TenantAgent | null | undefined>(undefined);

  React.useEffect(() => {
    let live = true;
    fetchTenantAgents()
      .then((agents) => { if (live) setAgent(agents.find((a) => a.kind === kind && a.ready) ?? null); })
      .catch(() => { if (live) setAgent(null); });
    return () => { live = false; };
  }, [kind]);

  if (agent === undefined) {
    // The header and a section's outline while the agent is found.
    return (
      <Stack spacing={2} aria-hidden>
        <Stack spacing={0.5}>
          <Skeleton variant="text" width={220} height={32} />
          <Skeleton variant="text" width={420} />
        </Stack>
        <Panel title=" " loading loadingHeight={96}>{null}</Panel>
      </Stack>
    );
  }
  if (agent === null) {
    return <EmptyState message="This workspace doesn't have that agent. Open Agents to see the ones it has." />;
  }
  return <AgentBody agent={agent} />;
}

function AgentBody({ agent }: { agent: TenantAgent }) {
  const { section = 'spotlight', itemId } = useParams();

  const pages = PAGES[agent.kind] ?? PAGES.rocco;
  const Section = pages[section] || pages.spotlight;

  // One quote, opened from the list or straight after an upload, has the page to itself.
  if (agent.kind === 'carrier_quoting' && section === 'quotes' && itemId) {
    return <QuotePage quoteId={itemId} />;
  }

  // One employer account, opened from the Accounts list or from a request about it.
  if (agent.kind === 'policy_servicing' && section === 'accounts' && itemId) {
    return <PsAccountPage accountId={itemId} />;
  }

  return (
    <Stack spacing={2} sx={{ pb: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        <Stack spacing={0.25}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" sx={(t) => ({ color: t.tokens.text.primary })}>
              {titleOf(agent.kind, pages[section] ? section : 'spotlight')}
            </Typography>
            {(STREAMING[agent.kind] ?? []).includes(section) ? <StatusChip tone="good" label="Live" pulse /> : null}
          </Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {agent.description ? `${agent.name} · ${agent.description}` : agent.name}
          </Typography>
        </Stack>
      </Stack>

      <Box>
        <Section />
      </Box>
    </Stack>
  );
}
